import { useContext } from "react"
import UserContext from "@context/UserContext"

// new_placement_flow reaches the portal two ways: baked into the login session
// snapshot at first paint, and as a live override on every menu response. Both
// are resolved from the application container's channel — the flag is rolled out
// per channel and depends on nothing about the viewer. Gate UI on user.features,
// which holds the merged result.
// See postcard/docs/features/new-placement-flow.md.
export const FEATURE_NEW_PLACEMENT_FLOW = "new_placement_flow"

// Non-hook variant for class components
export const hasFeature = (user, name) => Array.isArray(user?.features) && user.features.includes(name)

// Merge the menu endpoint's live feature overrides into the session snapshot.
//
// Returns the *same* user object when nothing changes. This is load-bearing, not
// a micro-optimisation: getMenuInfo is a useCallback keyed on `user` and the
// effect that calls it depends on both, so returning a fresh object
// unconditionally makes the menu fetch re-run forever. Any refactor here has to
// preserve reference equality on a no-op.
export const mergeFeatureOverrides = (user, overrides) => {
  if (!overrides || typeof overrides !== "object") return user

  const current = Array.isArray(user?.features) ? user.features : []
  const merged = new Set(current)

  Object.entries(overrides).forEach(([name, enabled]) => {
    if (enabled) {
      merged.add(name)
    } else {
      merged.delete(name)
    }
  })

  const unchanged = merged.size === current.length && current.every((name) => merged.has(name))
  if (unchanged) return user

  return { ...user, features: [...merged] }
}

const useFeature = (name) => {
  const { user } = useContext(UserContext)
  return hasFeature(user, name)
}

export default useFeature
